import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
} from "firebase/firestore";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../../firebase";
import { Account, AccountType } from "../../../types/spend-tracker";
import { useAuth } from "../../components/authContext";

const AccountsPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAccount, setNewAccount] = useState<Partial<Account>>({
    name: "",
    type: "cash",
    provider: "",
    last4: "",
    isActive: true,
  });

  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  const loadAccounts = useCallback(async () => {
    if (!user) return;
    try {
      const accountsRef = collection(db, "users", user.uid, "accounts");
      const accountsSnap = await getDocs(accountsRef);
      const loadedAccounts = accountsSnap.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() } as Account)
      );
      setAccounts(
        loadedAccounts.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      );
    } catch (error) {
      console.error("Error loading accounts:", error);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadAccounts();
    }
  }, [user, loadAccounts]);

  const handleAddAccount = async () => {
    if (!user || !newAccount.name || !newAccount.type) {
      alert("Name and type are required");
      return;
    }

    try {
      const accountsRef = collection(db, "users", user.uid, "accounts");
      await addDoc(accountsRef, {
        name: newAccount.name,
        type: newAccount.type,
        provider: newAccount.provider || "",
        last4: newAccount.last4 || "",
        isActive: true,
        sortOrder: accounts.length,
      });

      alert("Account added successfully");
      setShowAddForm(false);
      setNewAccount({
        name: "",
        type: "cash",
        provider: "",
        last4: "",
        isActive: true,
      });
      loadAccounts();
    } catch (error) {
      console.error("Error adding account:", error);
      alert("Failed to add account");
    }
  };

  const handleDeleteAccount = async (accountId: string) => {
    if (!user) return;
    const confirmed = window.confirm(
      "Are you sure you want to delete this account? This will not delete historical balance entries."
    );
    if (!confirmed) return;

    try {
      const accountRef = doc(db, "users", user.uid, "accounts", accountId);
      await deleteDoc(accountRef);
      alert("Account deleted");
      loadAccounts();
    } catch (error) {
      console.error("Error deleting account:", error);
      alert("Failed to delete account");
    }
  };

  if (!user) return null;

  const accountsByType = {
    cash: accounts.filter((a) => a.type === "cash" && a.isActive),
    credit: accounts.filter((a) => a.type === "credit" && a.isActive),
    investment: accounts.filter((a) => a.type === "investment" && a.isActive),
  };

  return (
    <div style={{ minHeight: "100vh", padding: "2rem" }}>
      <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
        <div style={{ marginBottom: "2rem" }}>
          <h1
            style={{
              fontSize: "2.25rem",
              fontWeight: "bold",
              marginBottom: "0.5rem",
            }}
          >
            Manage Accounts
          </h1>
          <button
            onClick={() => navigate("/money")}
            style={{
              padding: "0.5rem 1rem",
              borderRadius: "0.375rem",
              border: "1px solid #ccc",
              marginTop: "0.5rem",
              cursor: "pointer",
            }}
          >
            ← Back to Dashboard
          </button>
        </div>

        {/* Accounts List */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "0.5rem",
            border: "1px solid #e5e7eb",
            padding: "1.5rem",
            marginBottom: "2rem",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "1rem",
            }}
          >
            <h2 style={{ fontSize: "1.25rem", fontWeight: "600" }}>
              Your Accounts
            </h2>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              style={{
                padding: "0.5rem 1rem",
                borderRadius: "0.375rem",
                backgroundColor: "#2563eb",
                color: "white",
                border: "none",
                cursor: "pointer",
              }}
            >
              {showAddForm ? "Cancel" : "Add Account"}
            </button>
          </div>

          {showAddForm && (
            <div
              style={{
                marginBottom: "1.5rem",
                padding: "1rem",
                border: "1px solid #e5e7eb",
                borderRadius: "0.5rem",
                backgroundColor: "#f9fafb",
              }}
            >
              <h3 style={{ fontWeight: "600", marginBottom: "1rem" }}>
                Add New Account
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "1rem",
                }}
              >
                <input
                  placeholder="Account Name"
                  value={newAccount.name}
                  onChange={(e) =>
                    setNewAccount({ ...newAccount, name: e.target.value })
                  }
                  style={{
                    padding: "0.5rem",
                    borderRadius: "0.375rem",
                    border: "1px solid #ccc",
                  }}
                />
                <select
                  value={newAccount.type}
                  onChange={(e) =>
                    setNewAccount({
                      ...newAccount,
                      type: e.target.value as AccountType,
                    })
                  }
                  style={{
                    padding: "0.5rem",
                    borderRadius: "0.375rem",
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="cash">Cash/Checking/Savings</option>
                  <option value="credit">Credit Card</option>
                  <option value="investment">Investment</option>
                </select>
                <input
                  placeholder="Provider (optional)"
                  value={newAccount.provider}
                  onChange={(e) =>
                    setNewAccount({ ...newAccount, provider: e.target.value })
                  }
                  style={{
                    padding: "0.5rem",
                    borderRadius: "0.375rem",
                    border: "1px solid #ccc",
                  }}
                />
                <input
                  placeholder="Last 4 digits (optional)"
                  value={newAccount.last4}
                  maxLength={4}
                  onChange={(e) =>
                    setNewAccount({ ...newAccount, last4: e.target.value })
                  }
                  style={{
                    padding: "0.5rem",
                    borderRadius: "0.375rem",
                    border: "1px solid #ccc",
                  }}
                />
              </div>
              <button
                onClick={handleAddAccount}
                style={{
                  padding: "0.5rem 1rem",
                  borderRadius: "0.375rem",
                  backgroundColor: "#2563eb",
                  color: "white",
                  marginTop: "1rem",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Save Account
              </button>
            </div>
          )}

          {["cash", "credit", "investment"].map((type) => (
            <div key={type} style={{ marginBottom: "1.5rem" }}>
              <h3
                style={{
                  fontWeight: "600",
                  marginBottom: "0.75rem",
                  textTransform: "capitalize",
                }}
              >
                {type === "cash"
                  ? "Cash Accounts"
                  : type === "credit"
                  ? "Credit Cards"
                  : "Investment Accounts"}
              </h3>
              {accountsByType[type as AccountType].length === 0 ? (
                <p style={{ fontSize: "0.875rem", color: "#888" }}>
                  No {type} accounts yet
                </p>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                  }}
                >
                  {accountsByType[type as AccountType].map((account) => (
                    <div
                      key={account.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "0.75rem",
                        border: "1px solid #e5e7eb",
                        borderRadius: "0.5rem",
                      }}
                    >
                      <div>
                        <p style={{ fontWeight: "500" }}>{account.name}</p>
                        <p style={{ fontSize: "0.875rem", color: "#888" }}>
                          {account.provider && `${account.provider} • `}
                          {account.last4 && `****${account.last4}`}
                        </p>
                      </div>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button
                          onClick={() => handleDeleteAccount(account.id)}
                          style={{
                            padding: "0.25rem 0.75rem",
                            borderRadius: "0.375rem",
                            border: "1px solid #ccc",
                            fontSize: "0.875rem",
                            cursor: "pointer",
                            backgroundColor: "white",
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div
          style={{
            backgroundColor: "#f0f9ff",
            borderRadius: "0.5rem",
            border: "1px solid #bfdbfe",
            padding: "1rem",
            marginBottom: "2rem",
          }}
        >
          <p style={{ fontSize: "0.875rem", color: "#1e40af" }}>
            💡 <strong>Tip:</strong> Add your accounts here, then go back to the
            dashboard to create entries with balance snapshots for each date you
            want to track.
          </p>
        </div>

        <div style={{ marginTop: "2rem", textAlign: "center" }}>
          <button
            onClick={logout}
            style={{
              fontSize: "0.875rem",
              color: "#888",
              textDecoration: "underline",
              cursor: "pointer",
              background: "none",
              border: "none",
            }}
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountsPage;
